import React, { useMemo } from "react";
import { useDispatch } from "./redux/store";
import {
  updateAppointment,
  resetAppointment,
} from "./redux/slices/appointmentSlice";
import { sendToWindowDataLayer } from "./utils";
import { formatHoursOfOperation, formatPhoneNumber } from "./utils/formatting";
import {
  buildBailoutPayload,
  captureBailout,
  hasCallbackNumber,
} from "./utils/bailout";
import tinycolor from "tinycolor2";
import { AppointmentState } from "./types";
import {
  ACTIVE_BOOKING_STAGES,
  BookingStage,
  REDIRECT_STAGES,
} from "./constants/bookingStages";

interface HeaderProps {
  appointment: AppointmentState;
}

// Extend AppointmentState to include previousStage property
interface ExtendedAppointmentState extends AppointmentState {
  previousStage?: number;
}

const Header: React.FC<HeaderProps> = ({ appointment }) => {
  const dispatch = useDispatch();

  const isStageZero = appointment?.stage === BookingStage.Location;

  const headerColor = useMemo((): string => {
    if (isStageZero) {
      return "#ffffff";
    }
    if (appointment?.shopData?.headerColor) {
      return appointment.shopData.headerColor;
    }
    return "#ffffff";
  }, [appointment?.shopData?.headerColor, isStageZero]);

  const fontColor = useMemo((): string => {
    return tinycolor(headerColor).isDark() ? "#ffffff" : "#000000";
  }, [headerColor]);
  const isHeaderDark = useMemo((): boolean => {
    return tinycolor(headerColor).isDark();
  }, [headerColor]);
  const stageOneTextColor = fontColor;
  const stageOneSecondaryTextColor = useMemo((): string => {
    return tinycolor(fontColor).setAlpha(isHeaderDark ? 0.82 : 0.7).toRgbString();
  }, [fontColor, isHeaderDark]);
  const stageOneCloseButtonBorderColor = isHeaderDark
    ? "rgba(255,255,255,0.35)"
    : "#d1d5db";
  const stageOneCloseButtonBg = isHeaderDark
    ? "rgba(255,255,255,0.12)"
    : "#ffffff";
  const stageOneCloseIconColor = isHeaderDark ? "#ffffff" : "#4b5563";
  // Mobile slim bar only (src/Header.tsx isStageOne mobile block) — kept
  // separate from stageOneTextColor/stageOneSecondaryTextColor above since
  // the desktop block also reads those and this redesign is mobile-only.
  // Used for both the phone link and the address text.
  const mobileBarSecondaryColor = isHeaderDark
    ? "rgba(255,255,255,0.82)"
    : "#4b5563";
  const stageZeroTextColor = "#2b73b9";
  const stageZeroSubtitleColor = "#4b5563";
  const stageZeroAccentColor = "#2b73b9";

  const hoursText = useMemo(
    () => formatHoursOfOperation(appointment?.shopData?.shopSchedule),
    [appointment?.shopData?.shopSchedule]
  );

  const handleBailOut = async (): Promise<void> => {
    if (REDIRECT_STAGES.includes(appointment.stage ?? -1)) {
      const outlet =
        appointment?.shopData?.shopContact?.socialLinks?.[0]?.url || "";
      if (outlet) {
        window.location.replace(outlet);
      }
    } else {
      if (appointment.bailed || appointment.stage === BookingStage.Bailout) {
        const isInIframe = window.self !== window.top;

        // Closing the bailout page without pressing "Request Help" still
        // leaves a lead, but only when there is a number to call back.
        if (
          appointment.stage === BookingStage.Bailout &&
          !appointment.bailed &&
          hasCallbackNumber(appointment.customer?.phone)
        ) {
          try {
            const bailoutData = buildBailoutPayload({
              appointment,
              customer: {
                phone: appointment.customer?.phone,
                fname: appointment.customer?.fname,
                lname: appointment.customer?.lname,
              },
              comments:
                (appointment as any)?.bailOutStage?.bailOutComments ||
                "Exited from bailout page without submitting form",
            });

            await captureBailout({
              appointment,
              payload: bailoutData,
              dispatch,
              updateAppointmentAction: updateAppointment,
            });
            sendToWindowDataLayer("dc-service-bailed", "dc-service-bailed");
          } catch (error) {
            console.error("Error capturing bailout data:", error);
          }
        }

        let dispatchData: Partial<AppointmentState>;
        if (isInIframe) {
          dispatchData = {
            stage: appointment.entryStage,
            domain: appointment.domain,
            rwg_token: appointment.rwg_token,
            entryStage: appointment.entryStage,
            specId: appointment.specId,
          };

          if (appointment.specId === 0) {
            dispatchData = { ...dispatchData, shopData: appointment.shopData };
          } else if (appointment.specId === 1) {
            dispatchData = {
              stage: BookingStage.Location,
              domain: appointment.domain,
              rwg_token: appointment.rwg_token,
              entryStage: 0,
              specId: appointment.specId,
            };
          }

          dispatch(resetAppointment({ dispatchData: dispatchData }));
          window.parent.postMessage("hideWidget", "*");
        } else {
          if (
            appointment?.customer?.phone ||
            appointment?.customer?.fname ||
            appointment?.customer?.lname
          ) {
            sendToWindowDataLayer(
              "dc-service-partial_conversion",
              "dc-service-partial_conversion"
            );
          }

          const redirect =
            appointment?.shopData?.shopContact?.socialLinks?.[0]?.url || "";
          if (redirect) {
            window.location.replace(redirect);
          }
        }
      } else {
        // Use the extended appointment state to include previousStage
        const updates: Partial<ExtendedAppointmentState> = {
          stage: BookingStage.Bailout,
          previousStage: appointment.stage,
        };

        dispatch(
          updateAppointment({
            updates: updates as Partial<AppointmentState>, // Type assertion to match the expected type
          })
        );
      }
    }
  };

  if (isStageZero) {
    return (
      <div
        className="relative flex flex-col gap-3 border-b border-gray-200 px-4 py-4 md:px-8 md:py-6"
        style={{
          backgroundColor: headerColor,
        }}
      >
        <div className="absolute right-4 top-4 md:right-6 md:top-6">
          <button
            type="button"
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-2 border-solid text-lg font-semibold bg-white hover:bg-gray-100 transition-colors focus:outline-none focus:ring-0 ring-0 shadow-none"
            style={{
              borderColor: "#d1d5db",
              boxShadow: "none",
            }}
            onClick={handleBailOut}
            data-testid="bailout-close-button"
            aria-label="Close booking"
          >
            <i
              className="fa-solid fa-xmark"
              aria-hidden="true"
              style={{ color: "#4b5563", fontSize: "20px", lineHeight: 1 }}
            />
          </button>
        </div>

        <div className="flex flex-col gap-1 text-left md:text-center">
          <div className="flex items-start gap-3 md:items-center md:justify-center">
            <div
              className="mt-1 h-10 w-1 rounded-full md:hidden"
              style={{ backgroundColor: stageZeroAccentColor }}
            />
            <div>
              <div
                className="text-lg font-semibold md:text-2xl"
                style={{ color: stageZeroTextColor }}
              >
                Choose a location
              </div>
              <div
                className="text-sm md:text-base"
                style={{ color: stageZeroSubtitleColor }}
              >
                Select a Location to get started.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isStageOne = ACTIVE_BOOKING_STAGES.includes(appointment?.stage ?? -1);
  const shopConfig = appointment?.shopData;

  if (isStageOne) {
    const phoneNumber = shopConfig?.shopContact?.phone?.number || "";
    const formattedPhone = phoneNumber
      ? formatPhoneNumber(phoneNumber) || phoneNumber
      : "";
    const address = shopConfig?.shopContact?.address;
    const shopName = shopConfig?.shopName?.name || "";
    const mobileBarStreetLine = address?.street || "";
    const mobileBarCityStateZip = address
      ? [address.city, [address.state, address.zip].filter(Boolean).join(" ")]
          .filter(Boolean)
          .join(", ")
      : "";

    return (
      <div
        className="border-b border-gray-200 px-3 py-2.5 md:min-h-0 md:px-6 md:py-4"
        style={{ backgroundColor: headerColor }}
      >
        {/* Mobile layout: logo, then a stacked address/phone column (street,
            then city/state/zip, then phone — the way a US address is
            normally read), then close. Each line ellipsizes independently so
            a long street name or city doesn't blow out the row. */}
        <div className="flex items-center gap-2.5 md:hidden">
          {shopConfig?.shopLogo?.logoUrl && (
            <img
              src={shopConfig.shopLogo.logoUrl}
              alt="company logo"
              className="flex-shrink-0"
              style={{ height: "26px", width: "auto" }}
            />
          )}
          <div className="min-w-0 flex-1">
            {mobileBarStreetLine && (
              <div
                className="overflow-hidden whitespace-nowrap text-ellipsis text-xs leading-tight"
                style={{ color: mobileBarSecondaryColor }}
                title={mobileBarStreetLine}
              >
                {mobileBarStreetLine}
              </div>
            )}
            {mobileBarCityStateZip && (
              <div
                className="overflow-hidden whitespace-nowrap text-ellipsis text-xs leading-tight"
                style={{ color: mobileBarSecondaryColor }}
                title={mobileBarCityStateZip}
              >
                {mobileBarCityStateZip}
              </div>
            )}
            {formattedPhone && (
              <a
                href={`tel:${phoneNumber.replace(/\D/g, "")}`}
                className="inline-flex items-center gap-[5px] whitespace-nowrap text-xs font-medium leading-tight no-underline"
                style={{ color: mobileBarSecondaryColor }}
              >
                <i
                  className="fa-solid fa-phone flex-shrink-0"
                  aria-hidden="true"
                  style={{ fontSize: "11px", color: "#6b7280" }}
                />
                <span>{formattedPhone}</span>
              </a>
            )}
          </div>
          {/* Close button (mobile) */}
          <button
            type="button"
            className="flex h-[34px] w-[34px] flex-shrink-0 cursor-pointer items-center justify-center rounded-full border-[1.5px] border-solid transition-opacity hover:opacity-90 focus:outline-none focus:ring-0 ring-0 shadow-none"
            style={{
              borderColor: stageOneCloseButtonBorderColor,
              backgroundColor: stageOneCloseButtonBg,
              boxShadow: "none",
            }}
            onClick={handleBailOut}
            data-testid="bailout-close-button"
            aria-label="Close booking"
          >
            <i
              className="fa-solid fa-xmark"
              aria-hidden="true"
              style={{ color: stageOneCloseIconColor, fontSize: "16px", lineHeight: 1 }}
            />
          </button>
        </div>

        {/* Desktop / Tablet layout: 3 balanced columns with close button inside right column */}
        <div className="hidden md:grid md:grid-cols-3 md:items-center md:gap-4">
          {/* Left: Location address */}
          <div className="text-left" style={{ color: stageOneTextColor }}>
            <div className="text-sm font-semibold leading-tight">{shopName}</div>
            {address && (
              <div
                className="mt-1 text-xs leading-snug"
                style={{ color: stageOneSecondaryTextColor }}
              >
                <div>{address.street}</div>
                <div>
                  {address.city}, {address.state} {address.zip}
                </div>
              </div>
            )}
          </div>

          {/* Center: Logo */}
          <div className="flex justify-center">
            {shopConfig?.shopLogo?.logoUrl ? (
              <img
                src={shopConfig.shopLogo.logoUrl}
                alt="company logo"
                style={{ width: "auto", height: "64px" }}
              />
            ) : (
              <div className="text-xl font-semibold" style={{ color: stageOneTextColor }}>
                {shopName}
              </div>
            )}
          </div>

          {/* Right: Phone + Hours + Close button */}
          <div className="flex items-center justify-end gap-3">
            <div className="text-right" style={{ color: stageOneTextColor }}>
              {formattedPhone && (
                <div className="text-sm font-semibold">
                  <a
                    href={`tel:${phoneNumber.replace(/\D/g, "")}`}
                    className="text-inherit no-underline hover:underline"
                    style={{ color: "inherit" }}
                  >
                    {formattedPhone}
                  </a>
                </div>
              )}
              {hoursText && (
                <div
                  className="mt-1 text-xs leading-snug whitespace-pre-line"
                  style={{ color: stageOneSecondaryTextColor }}
                >
                  {hoursText.split("  |  ").map((line: string, i: number) => (
                    <div key={i}>{line}</div>
                  ))}
                </div>
              )}
            </div>

            {/* Close button (desktop) */}
            <button
              type="button"
              className="flex-shrink-0 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-2 border-solid text-lg font-semibold transition-opacity hover:opacity-90 focus:outline-none focus:ring-0 ring-0 shadow-none"
              style={{
                borderColor: stageOneCloseButtonBorderColor,
                backgroundColor: stageOneCloseButtonBg,
                boxShadow: "none",
              }}
              onClick={handleBailOut}
              data-testid="bailout-close-button"
              aria-label="Close booking"
            >
              <i
                className="fa-solid fa-xmark"
                aria-hidden="true"
                style={{ color: stageOneCloseIconColor, fontSize: "20px", lineHeight: 1 }}
              />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // All stages are now handled by isStageZero or isStageOne above
  return null;
};

export default Header;
